import { motion } from "framer-motion";
import { ArrowUpRight, Megaphone, Calendar } from "lucide-react";
import AbstractShapes from "@/components/landing/AbstractShapes";

const COLLECTIONS = [
  {
    id: "surgical-innovation",
    journalId: "ijsm",
    journalShort: "IJSM",
    title: "Surgical Innovation in Resource-Limited Settings",
    description:
      "Case series, prospective studies and reviews on cost-conscious surgical innovation, low-resource perioperative care and global surgery initiatives.",
    deadline: "August 31, 2026",
    submitUrl: "https://www.ejmanager.com/my/ijsm/",
    received: 18,
    target: 30,
    accent: "var(--sci-gold)",
    open: true,
  },
  {
    id: "translational-case-reports",
    journalId: "ijmrcr",
    journalShort: "IJMRCR",
    title: "Bridging Bench to Bedside · Translational Case Reports",
    description:
      "High-impact translational clinical case reports across all medical specialties — molecular, genomic and pharmacogenomic mechanisms paired with patient outcomes.",
    deadline: "September 15, 2026",
    submitUrl: "https://www.ejmanager.com/my/ijmrcr/",
    received: 24,
    target: 40,
    accent: "var(--sci-teal)",
    open: true,
  },
  {
    id: "ai-clinical-decisions",
    journalId: "both",
    journalShort: "IJSM + IJMRCR",
    title: "Artificial Intelligence in Clinical Decision Support",
    description:
      "Original studies and case reports applying AI/ML to diagnosis, triage, prognosis or surgical planning — with validation against established clinical endpoints.",
    deadline: "October 30, 2026",
    submitUrl: "https://www.ejmanager.com/my/ijmrcr/",
    received: 9,
    target: 25,
    accent: "var(--sci-navy)",
    open: true,
  },
];

function progressPct(rec, tgt) {
  return Math.min(100, Math.round((rec / tgt) * 100));
}

export default function CallForPapers() {
  return (
    <section
      id="call-for-papers"
      data-testid="call-for-papers-section"
      className="relative py-24 md:py-32 bg-[var(--sci-bg)] overflow-hidden"
    >
      <AbstractShapes
        variant="arcs"
        tone="ink"
        className="left-[-100px] top-0 w-[420px] h-[420px] opacity-50"
      />
      <AbstractShapes
        variant="rings"
        tone="ink"
        className="right-[-80px] bottom-0 w-[420px] h-[420px] opacity-50"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-16">
          <div>
            <div className="overline text-[var(--sci-teal)] mb-5 flex items-center gap-2">
              <Megaphone size={14} /> · Call for Papers · 2026
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02] max-w-3xl">
              Open <em className="not-italic text-[var(--sci-teal)]">collections</em>.
            </h2>
          </div>
          <p className="md:max-w-md text-[var(--sci-text-muted)] leading-relaxed">
            Curated topical collections currently accepting submissions. Guest editors review every contribution before peer review opens.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {COLLECTIONS.map((c, i) => {
            const pct = progressPct(c.received, c.target);
            return (
              <motion.a
                key={c.id}
                href={c.submitUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`cfp-card-${c.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, delay: 0.07 * i }}
                className="group relative bg-white border border-[var(--sci-border)] card-lift p-7 md:p-8 overflow-hidden flex flex-col"
              >
                <div
                  className="absolute top-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                  style={{ background: c.accent }}
                />

                <div className="flex items-start justify-between gap-3 mb-5">
                  <div className="flex items-center gap-2 text-[11px] font-mono-sci tracking-[0.18em] uppercase text-[var(--sci-teal)] border border-[var(--sci-teal)]/30 px-2 py-1">
                    Accepting Submissions
                  </div>
                  <div className="font-mono-sci text-xs text-[var(--sci-text-muted)]">
                    {String(i + 1).padStart(2, "0")} / 03
                  </div>
                </div>

                <div className="overline text-[var(--sci-text-muted)] mb-2">
                  {c.journalShort}
                </div>

                <h3 className="font-serif-display text-xl md:text-2xl text-[var(--sci-navy)] tracking-tight leading-tight group-hover:text-[var(--sci-teal)] transition-colors">
                  {c.title}
                </h3>
                <p className="mt-3 text-sm text-[var(--sci-text-muted)] leading-relaxed">
                  {c.description}
                </p>

                {/* Progress bar */}
                <div className="mt-7">
                  <div className="flex items-center justify-between font-mono-sci text-[10px] tracking-wider text-[var(--sci-text-muted)] mb-2">
                    <span data-testid={`cfp-received-${c.id}`}>
                      {c.received} RECEIVED
                    </span>
                    <span>{c.target} TARGET · {pct}%</span>
                  </div>
                  <div className="h-1 w-full bg-[var(--sci-border)] overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{ width: `${pct}%`, background: c.accent }}
                    />
                  </div>
                </div>

                <div className="mt-7 pt-5 border-t border-[var(--sci-border)] flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-[var(--sci-text-muted)]">
                    <Calendar size={13} className="text-[var(--sci-teal)]" />
                    <span data-testid={`cfp-deadline-${c.id}`}>{c.deadline}</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--sci-navy)] group-hover:text-[var(--sci-teal)] transition-colors">
                    Submit
                    <ArrowUpRight size={14} />
                  </span>
                </div>
              </motion.a>
            );
          })}
        </div>

        <div className="mt-10 text-xs text-[var(--sci-text-muted)] max-w-3xl leading-relaxed">
          <span className="font-mono-sci text-[10px] tracking-wider text-[var(--sci-teal)] mr-2">
            PROPOSE A COLLECTION
          </span>
          Editors or institutions interested in guest-editing a thematic collection can reach the editorial office through the journal portals at{" "}
          <a
            href="https://www.mdpub.net"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--sci-navy)] underline decoration-dotted hover:text-[var(--sci-teal)]"
          >
            mdpub.net
          </a>{" "}
          or{" "}
          <a
            href="https://www.ejos.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--sci-navy)] underline decoration-dotted hover:text-[var(--sci-teal)]"
          >
            ejos.org
          </a>
          .
        </div>
      </div>
    </section>
  );
}
